import { FormInstance } from 'antd';
import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Skeleton } from 'src/common';
import { useContentLibrary } from 'src/models/bids/ContentLibrary/hooks';
import { IAnswerFormState } from '../BidTaskAnswer';
import { ContentLibraryBox } from './ContentLibraryBox';
import styles from './index.module.scss';
import { BlockTitle } from 'src/shared';
import { TASK_EDITOR_ICON_MAPPER } from 'src/models/bids/BidTask/mappers';
import { useTask } from 'src/models/bids/BidTask/hooks';
import { trackBTShowMore } from 'src/segment/events';
import { BidQuestionType } from 'src/models/bids/BidFull/types';

interface IProps {
  form: FormInstance<IAnswerFormState>;
  setIsFocused: (isFocused: boolean) => void;
}

export const ContentLibrary: FC<IProps> = ({ form, setIsFocused }) => {
  const { t } = useTranslation();
  const { data: task } = useTask();
  const { data: state, offset, limit, setOffset } = useContentLibrary();
  const content = useMemo(() => (state?.data ? state.data.content : []), [state?.data]);
  const isMore = useMemo(() => state?.data && offset + limit <= state.data.totalCount, [limit, offset, state?.data]);

  const onShowMore = useCallback(() => {
    if (task) {
      trackBTShowMore(
        task.id,
        task.groupId,
        task.bidId,
        task.isGenerated ? BidQuestionType.Generated : BidQuestionType.Manual
      );
    }
    state?.fetchMore({ variables: { offset: offset + limit } }).then(() => setOffset(offset + limit));
  }, [task, state, offset, limit, setOffset]);

  return state?.loading ? (
    <Skeleton active loading />
  ) : state?.data ? (
    <div className={styles.container}>
      <BlockTitle
        title={t('BiddingTool.contentLibrary')}
        icon={TASK_EDITOR_ICON_MAPPER['lib']}
        className={styles.title}
      />
      <div className={styles.content}>
        {!!content.length ? (
          content.map(c => c && <ContentLibraryBox key={c.id} content={c} form={form} setIsFocused={setIsFocused} />)
        ) : (
          <span className={styles.desc}>{t('BiddingTool.noContentLibData')}</span>
        )}
      </div>
      {isMore && (
        <div className={styles.showMore}>
          <Button type={'link'} onClick={onShowMore} loading={state?.fetchingMore}>
            {t('Common.showMore')}
          </Button>
        </div>
      )}
    </div>
  ) : null;
};

export default ContentLibrary;
